import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Minus, Utensils } from "lucide-react";
import { useOrder, API } from "../context/OrderContext";
import { formatEuro, formatPickupDate, computeTotals, CUTLERY_PRICE } from "../lib/menu";
import { track, getFbCookies } from "../lib/meta";

export default function Checkout() {
  const navigate = useNavigate();
  const { order, update, menu, config } = useOrder();
  const { quantities, pickupDate, pickupLabel, timeSlot, categoryPickups = {}, cutlery = 0 } = order;

  const { total, vat, totalPortions, cutleryTotal } = computeTotals(quantities, menu, cutlery);
  const selected = menu.filter((d) => (quantities[d.id] || 0) > 0);

  const cats = config?.menu_categories || [];
  const selectedCatKeys = [...new Set(selected.map((d) => d.category || "dinner"))];
  const scheduledCats = cats.filter((c) => c.has_schedule && selectedCatKeys.includes(c.key));
  const hasGlobal = selectedCatKeys.some((k) => {
    const c = cats.find((x) => x.key === k);
    return !c || !c.has_schedule;
  });

  const pickupRows = [];
  scheduledCats.forEach((c) => {
    const cp = categoryPickups[c.key];
    pickupRows.push({ label: c.label, value: cp?.label && cp?.slot ? `${cp.label} · ${cp.slot}` : "—" });
  });
  if (hasGlobal) {
    pickupRows.push({ label: scheduledCats.length ? "Overige gerechten" : "Ophalen", value: pickupLabel && timeSlot ? `${pickupLabel} · ${timeSlot}` : "—" });
  }

  const [form, setForm] = useState({ naam: "", email: "", telefoon: "", opmerkingen: "" });
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);

  const setCutlery = (v) => update({ cutlery: Math.max(0, v) });

  useEffect(() => {
    if (totalPortions > 0) {
      track("InitiateCheckout", {
        value: total,
        currency: "EUR",
        num_items: totalPortions,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  if (selected.length === 0) {
    return (
      <div className="max-w-[1200px] mx-auto w-full px-6 py-20 text-center">
        <p className="font-[600] text-[#0D3B4C] mb-6">Je hebt nog geen gerechten geselecteerd.</p>
        <Link to="/menu-samenstellen" className="bg-[#B71C1C] text-white font-[700] uppercase py-4 px-8 inline-block">
          Kies je gerecht(en)
        </Link>
      </div>
    );
  }

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!form.naam || !form.email || !form.telefoon) {
      toast.error("Vul je naam, e-mail en telefoon in.");
      return;
    }
    const scheduledOk = scheduledCats.every((c) => categoryPickups[c.key]?.date && categoryPickups[c.key]?.slot);
    const globalOk = !hasGlobal || (pickupDate && timeSlot);
    if (!scheduledOk || !globalOk) {
      toast.error("Kies een ophaaldag en tijdslot voor elke categorie.");
      navigate("/menu-samenstellen");
      return;
    }
    if (!agree) {
      toast.error("Ga akkoord met de algemene voorwaarden.");
      return;
    }
    setLoading(true);
    try {
      const { fbp, fbc } = getFbCookies();
      const category_pickups = {};
      scheduledCats.forEach((c) => {
        const cp = categoryPickups[c.key];
        category_pickups[c.key] = { date: cp.date, slot: cp.slot };
      });
      const res = await axios.post(`${API}/checkout`, {
        quantities,
        pickup_date: hasGlobal ? pickupDate : null,
        time_slot: hasGlobal ? timeSlot : null,
        category_pickups,
        customer: form,
        cutlery,
        fbp,
        fbc,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Er ging iets mis. Probeer het opnieuw.");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[900px] mx-auto w-full px-6 py-12">
      <h1 className="text-4xl sm:text-5xl font-[700] text-[#0D3B4C] tracking-tight text-center mb-10">Bestellen</h1>

      {/* Summary */}
      <div className="bg-[#E8F5E9] p-6 mb-8" data-testid="checkout-summary">
        <h2 className="text-xl font-[700] text-[#0D3B4C] mb-4">Jouw bestelling</h2>
        <div className="space-y-2">
          {selected.map((d) => (
            <div key={d.id} className="flex justify-between font-[600] text-[#0D3B4C]">
              <span>{d.name} × {quantities[d.id]}</span>
              <span>{formatEuro(quantities[d.id] * d.price)}</span>
            </div>
          ))}
        </div>
        <div className="border-t-2 border-white my-4" />
        {cutlery > 0 && (
          <div className="flex justify-between font-[600] text-[#0D3B4C]" data-testid="summary-cutlery-line">
            <span>Bestek × {cutlery}</span>
            <span>{formatEuro(cutleryTotal)}</span>
          </div>
        )}
        {pickupRows.map((row, i) => (
          <div key={i} className="flex justify-between font-[600] text-[#0D3B4C]">
            <span>{row.label}</span>
            <span data-testid={`checkout-pickup-${i}`}>{row.value}</span>
          </div>
        ))}
        <div className="flex justify-between font-[600] text-[#0D3B4C]"><span>Totaal porties (personen)</span><span>{totalPortions}</span></div>
        <div className="flex justify-between text-2xl font-[700] text-[#0D3B4C] mt-3"><span>Totaal</span><span data-testid="summary-total">{formatEuro(total)}</span></div>
        <p className="text-sm text-[#0D3B4C]">waarvan 9% BTW: {formatEuro(vat)}</p>
      </div>

      {/* Customer form */}
      <div className="space-y-4">
        {[
          { k: "naam", label: "Naam *", type: "text", tid: "input-naam" },
          { k: "email", label: "E-mail *", type: "email", tid: "input-email" },
          { k: "telefoon", label: "Telefoon *", type: "tel", tid: "input-telefoon" },
        ].map((f) => (
          <div key={f.k}>
            <label className="block font-[600] text-[#0D3B4C] mb-1">{f.label}</label>
            <input
              type={f.type}
              value={form[f.k]}
              onChange={set(f.k)}
              data-testid={f.tid}
              className="w-full border-2 border-[#0D3B4C] bg-white px-4 py-3 font-[400] focus:border-[#B71C1C] focus:outline-none"
            />
          </div>
        ))}
      </div>

      <label className="flex items-start gap-3 mt-6 cursor-pointer">
        <input
          type="checkbox"
          checked={agree}
          onChange={(e) => setAgree(e.target.checked)}
          data-testid="agree-checkbox"
          className="mt-1 w-5 h-5 accent-[#B71C1C]"
        />
        <span className="font-[400] text-[#0D3B4C]">
          Ik ga akkoord met de{" "}
          <Link to="/algemene-voorwaarden" className="font-[600] text-[#1B6B2E] underline">algemene voorwaarden</Link>
        </span>
      </label>

      <p className="mt-4">
        <Link to="/allergenen" data-testid="allergenen-link" className="font-[600] text-[#1B6B2E] underline">
          Bekijk de allergeneninformatie per gerecht
        </Link>
      </p>

      {/* Bestek meebestellen — net boven de bestelknop */}
      <div className="mt-8 border-2 border-[#1B6B2E] bg-white">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-5" data-testid="cutlery-selector">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-11 h-11 rounded-full bg-[#E8F5E9] text-[#1B6B2E] flex items-center justify-center flex-shrink-0">
              <Utensils size={20} />
            </div>
            <div>
              <div className="font-[700] text-[#0D3B4C]">Bestek meebestellen</div>
              <div className="text-sm font-[400] text-[#0D3B4C]">Mes, vork &amp; servet — {formatEuro(CUTLERY_PRICE)} per set</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setCutlery(cutlery - 1)}
              disabled={cutlery === 0}
              data-testid="cutlery-dec"
              aria-label="Minder bestek"
              className="w-11 h-11 rounded-full bg-[#1B6B2E] text-white flex items-center justify-center hover:bg-[#144F22] transition-colors disabled:opacity-40"
            >
              <Minus size={20} />
            </button>
            <span className="w-12 text-center font-[700] text-2xl text-[#0D3B4C]" data-testid="cutlery-count">{cutlery}</span>
            <button
              onClick={() => setCutlery(cutlery + 1)}
              data-testid="cutlery-inc"
              aria-label="Meer bestek"
              className="w-11 h-11 rounded-full bg-[#1B6B2E] text-white flex items-center justify-center hover:bg-[#144F22] transition-colors"
            >
              <Plus size={20} />
            </button>
          </div>
        </div>
      </div>

      <button
        onClick={submit}
        disabled={loading}
        data-testid="pay-ideal"
        className="w-full mt-8 bg-[#B71C1C] text-white font-[700] uppercase tracking-wider py-4 hover:bg-[#8E1515] transition-colors disabled:opacity-50"
      >
        {loading ? "Bezig..." : "Betalen met iDEAL/WERO"}
      </button>
    </div>
  );
}
